import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";

import { Controller } from "../interface/controller.interface";
import { HTTPMethod } from "../model/http-method.enum";
import { HTTPRequestBuilder } from "../model/http.request.model";
import { HTTPService } from "./http.service";
import { TypeSystem } from "../support/type-system.support";
import { Order } from "../persistence/dto/order.model";
import { Order as OrderView } from "../persistence/view-dto/order.model";
import { orElse } from "../model/operator/or-else.operator";
import { UserFind } from "../persistence/dto/user-find.model";
import { ShippingPriceDTO } from "../core/models/shippingPriceDTO.model";
import { ResellerOrderDto } from "../core/models/resellerOrderDto.model";
import { CampaignLevelService } from "./campaign-level.service";

/**
 * @description
 *  Servicio reflejo del controlador de pedidos del backend.
 *
 * @see gigot-backend/Controllers/Orders/OrdersController.cs
 */

@Injectable({ providedIn: "root" })
export class OrderService implements Controller {

	constructor(
		private httpService: HTTPService,
		private campaignLevelService: CampaignLevelService) {}

	public basePath(): string {
		return "api/orders";
	}

	/**
	 * @description
	 *	Determina si un usuario puede crear un pedido según el área actual en
	 *	la cual se encuentra. Este método no tiene un endpoint equivalente en
	 *	el backend que realice la misma verificación.
	 */
	public canCreateOrderInArea(areaLevelId: number): Observable<boolean> {
		return this.campaignLevelService
			.areasByZoneInActiveCampaign(Math.floor(areaLevelId / 100))
			.pipe(map((areas: string[]) => 0 <= areas.findIndex((area: string) => (+area) === areaLevelId)));
	}

	public editOrder(orderId: number): Observable<typeof TypeSystem.EmptyObject> {
		return this.httpService.send<any, null>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath(`${orderId}/edit`)
				.build())
			.pipe(map(() => TypeSystem.EmptyObject));
	}

	public orderByID(orderId: number): Observable<OrderView> {
		return this.httpService.send<typeof undefined, OrderView>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`${orderId}`)
				.build())
			.pipe(map(orderView => OrderView.from(orderView)));
	}

	public ordersForOrderEvent(applicationUserId: string = ""): Observable<Order[]> {
		return this.httpService.send<typeof undefined, Order[]>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`forOrderEvent/${applicationUserId}`)
				.build())
			.pipe(
				orElse<Order[]>([] as Order[]),
				map(orders => orders.map(order => Order.from(order))));
	}

	public tryOrdersForOrderEvent(userFind: UserFind): Observable<Order[]> {
		if (userFind) {
			return this.ordersForOrderEvent(userFind.id);
		}
		else {
			return of([] as Order[]);
		}
	}

	public getShippingAmounts(campaignId: number): Observable<Array<ShippingPriceDTO>> {
		return this.httpService.send<typeof undefined, Array<ShippingPriceDTO>>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`getShippingAmounts/${campaignId}`)
				.build())
	}

	public findResellerOrdersByCampaign(campaignId: number, userId: String): Observable<Array<ResellerOrderDto>> {
		return this.httpService.send<typeof undefined, Array<ResellerOrderDto>>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`reseller/${userId}/campaign/${campaignId}`)
				.build())
	}
}
